import React, { useEffect, useRef, useState, useCallback } from 'react';
import { CATEGORY_ORDER, CAPABILITIES, getLiveCount, getTotalCount } from './ariaCapabilities';

/**
 * ExploreView — full catalog of what Aria can do.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────┐
 *   │ [Create] [Write] [Build] [Docs] [Money] ...   ←tabs  │
 *   ├──────────────────────────────────────────────────────┤
 *   │ Create                                               │
 *   │ ┌──┐ ┌──┐ ┌──┐ ┌──┐ ┌──┐ →                          │
 *   │ │  │ │  │ │  │ │  │ │  │   ← horizontal scroll       │
 *   │ └──┘ └──┘ └──┘ └──┘ └──┘                            │
 *   │                                                      │
 *   │ Write                                                │
 *   │ ┌──┐ ┌──┐ ┌──┐ →                                    │
 *   │ ...                                                  │
 *   └──────────────────────────────────────────────────────┘
 *
 * Click a tab → smooth-scroll to that row.
 * Scroll down → active tab updates automatically (IntersectionObserver).
 */

const SOURCE_PILL_BADGE = {
  live:   { label: '',     className: '' },
  beta:   { label: 'Beta', className: 'aria-cap-tile__badge--beta' },
  coming: { label: 'Soon', className: 'aria-cap-tile__badge--coming' },
};

function ExploreView() {
  const containerRef = useRef(null);
  const sectionRefs = useRef({});     // category id → DOM node
  const tabRefs = useRef({});         // category id → tab button DOM node
  const [activeCat, setActiveCat] = useState(CATEGORY_ORDER[0]?.id || null);
  const userClickedRef = useRef(false); // suppress observer right after a click

  const onPick = useCallback((cap) => {
    if (cap.route && cap.route.startsWith('#')) {
      try { window.location.hash = cap.route.slice(1); } catch (_) {}
      return;
    }
    if (cap.route && cap.route.endsWith('.html')) {
      // Open the workbench surface. App.jsx listens for this and renders the
      // matching WorkbenchFrame. We must NOT also fire prefillAndGoHome or
      // clear the hash here — those send the user back to the home screen so
      // the workbench never appears (the original "nothing pops up" bug).
      try {
        window.dispatchEvent(new CustomEvent('aria:openWorkbench', {
          detail: { src: '/' + cap.route, title: cap.title, prompt: cap.prompt }
        }));
      } catch (_) {}
      return;
    }
    try {
      window.dispatchEvent(new CustomEvent('aria:prefillAndGoHome', {
        detail: { prompt: cap.prompt || `Help me ${cap.title.toLowerCase()}` }
      }));
    } catch (_) {}
    try { window.location.hash = ''; } catch (_) {}
  }, []);

  const onTabClick = useCallback((catId) => {
    userClickedRef.current = true;
    setActiveCat(catId);
    const el = sectionRefs.current[catId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Re-enable observer-driven active-state after the smooth scroll settles.
    setTimeout(() => { userClickedRef.current = false; }, 700);
  }, []);

  // Observe which section is most-visible → set active tab. Skip while a tab
  // click is in flight so the observer doesn't fight the smooth-scroll.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (userClickedRef.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.dataset.cat;
          if (id) setActiveCat(id);
        }
      },
      { root: containerRef.current, rootMargin: '-80px 0px -50% 0px', threshold: [0.15, 0.5] }
    );
    for (const el of Object.values(sectionRefs.current)) {
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, []);

  // Keep the active tab scrolled into view in the tab bar itself.
  useEffect(() => {
    const el = tabRefs.current[activeCat];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeCat]);

  return (
    <div className="explore-view explore-view--catalog" ref={containerRef}>
      <header className="explore-catalog__header">
        <h1 className="explore-catalog__title">Explore</h1>
      </header>

      {/* One horizontally-scrolling row per category */}
      {CATEGORY_ORDER.map((cat) => {
        const caps = CAPABILITIES.filter((c) => c.category === cat.id);
        if (!caps.length) return null;
        return (
          <section
            key={cat.id}
            ref={(el) => { sectionRefs.current[cat.id] = el; }}
            data-cat={cat.id}
            className="explore-catalog__row"
          >
            <div className="explore-catalog__row-head">
              <h2 className="explore-catalog__row-title">{cat.label}</h2>
              <span className="explore-catalog__row-tag">{cat.tagline}</span>
            </div>
            {/* The big liquid-glass pane IS the button now. The 4 inner tiles
                are decorative previews of what's inside the category. Clicking
                anywhere on the pane opens the category. */}
            <button
              type="button"
              className="explore-catalog__row-track"
              onClick={() => onPick(caps[0])}
              aria-label={`Open ${cat.label}`}
            >
              {caps.map((cap) => {
                const badge = SOURCE_PILL_BADGE[cap.status] || SOURCE_PILL_BADGE.coming;
                return (
                  <div
                    key={cap.id}
                    className={`aria-cap-tile aria-cap-tile--${cap.status}`}
                    aria-hidden="true"
                  >
                    {/* Centered title ABOVE the glass card */}
                    <div className="aria-cap-tile__title">{cap.title}</div>
                    <div className="aria-cap-tile__art">
                      {badge.label && (
                        <span className={`aria-cap-tile__badge ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                      <span className="aria-cap-tile__art-emoji" aria-hidden="true">{cap.emoji}</span>
                      <span className="aria-cap-tile__art-label">{cap.title}</span>
                    </div>
                  </div>
                );
              })}
            </button>
          </section>
        );
      })}
    </div>
  );
}

export default React.memo(ExploreView);
